/**
 * Statuts Lead officiels (section 20 de `07-Leads-Quotes-Clients`).
 *
 * Seuls `NEW`, `CONTACTED`, `QUOTE_SENT`, `WON` et `LOST` possèdent une
 * correspondance legacy prouvée dans `docs/migration/status-mappings.md`.
 * `QUALIFIED`, `QUOTE_PREPARED` et `ARCHIVED` sont définis par la documentation
 * mais aucune donnée existante ne peut les produire aujourd'hui.
 */
export type LeadStatus =
  'NEW' | 'CONTACTED' | 'QUALIFIED' | 'QUOTE_PREPARED' | 'QUOTE_SENT' | 'WON' | 'LOST' | 'ARCHIVED'

export type LeadDataSource = 'fixtures' | 'supabase' | 'fixture-fallback'

/**
 * Vocabulaire d'issue d'appel (Sprint C, `leads.statut_v4`). Un troisième
 * vocabulaire, distinct de `statut`/`statut_v3` (voir
 * `docs/commercial/QUOTE_TO_CONTRACT_MIGRATION_AUDIT.md`, section 10) : plus
 * granulaire sur le suivi téléphonique, sans dérivation stricte de `statut_v3`.
 */
export type LeadStatusV4 =
  | 'NEW'
  | 'TO_CONTACT'
  | 'CALLBACK'
  | 'NO_ANSWER'
  | 'CONTACTED_NOT_INTERESTED'
  | 'INTERESTED'
  | 'CONVERTED'
  | 'ARCHIVED'

/** Les quatre issues qu'un appel sortant peut produire (section 9/69). */
export type LeadCallOutcome = 'INTERESTED' | 'CONTACTED_NOT_INTERESTED' | 'CALLBACK' | 'NO_ANSWER'

export interface RecordLeadCallOutcomeCommand {
  idempotencyKey: string
  leadId: string
  expectedVersion: number
  outcome: LeadCallOutcome
  /** Uniquement pour `CONTACTED_NOT_INTERESTED`, registre `leadLostReasons`. */
  reason?: LeadLostReason
  note?: string
  /** Requis pour `CALLBACK`. */
  callbackAt?: string
}

export interface RecordLeadCallOutcomeResult {
  leadId: string
  number: string | null
  statusV4: LeadStatusV4
  version: number
  changedAt: string
}

/** Liste fermée de la section 27, exploitable en statistiques de vente. */
export const leadLostReasons = [
  'PRICE',
  'NO_RESPONSE',
  'COMPETITOR',
  'OUT_OF_AREA',
  'SERVICE_NOT_OFFERED',
  'TIMING',
  'DUPLICATE',
  'OTHER',
] as const
export type LeadLostReason = (typeof leadLostReasons)[number]

/**
 * Transitions autorisées, strictement celles de la section 23. La documentation
 * ne prévoit pas `QUOTE_PREPARED → LOST` : la transition reste fermée tant
 * qu'elle ne l'ouvre pas.
 */
const allowedTransitions: Record<LeadStatus, LeadStatus[]> = {
  NEW: ['CONTACTED', 'LOST', 'ARCHIVED'],
  CONTACTED: ['QUALIFIED', 'LOST', 'ARCHIVED'],
  QUALIFIED: ['QUOTE_PREPARED', 'LOST', 'ARCHIVED'],
  QUOTE_PREPARED: ['QUOTE_SENT', 'ARCHIVED'],
  QUOTE_SENT: ['WON', 'LOST', 'ARCHIVED'],
  WON: ['ARCHIVED'],
  LOST: ['ARCHIVED'],
  ARCHIVED: [],
}

export function nextLeadStatuses(status: LeadStatus): LeadStatus[] {
  return allowedTransitions[status]
}

export function isLeadTransitionAllowed(from: LeadStatus, to: LeadStatus): boolean {
  return allowedTransitions[from].includes(to)
}

export interface ChangeLeadStatusCommand {
  idempotencyKey: string
  leadId: string
  expectedVersion: number
  toStatus: LeadStatus
  reason?: LeadLostReason
  note?: string
}

/**
 * Refonte Demandes, Phase 2 (audit UX/UI, §5.2) : corrige l'adresse et/ou
 * les coordonnées d'une Demande — « Vérifier l'adresse », « Relancer la
 * géolocalisation », pin manuel. Les deux coordonnées sont obligatoires : le
 * but même de la commande est de poser une position vérifiée.
 */
export interface UpdateLeadAddressCommand {
  idempotencyKey: string
  leadId: string
  expectedVersion: number
  latitude: number
  longitude: number
  address?: string
  city?: string
  postalCode?: string
}

export interface UpdateLeadAddressResult {
  leadId: string
  version: number
  latitude: number
  longitude: number
}

export interface LeadDuplicateSuggestion {
  clientId: string
  number: string | null
  displayName: string | null
  email: string | null
  phone: string | null
  matchedOn: 'PHONE' | 'EMAIL' | 'ADDRESS'
}

export interface ConvertLeadCommand {
  idempotencyKey: string
  leadId: string
  expectedVersion: number
  /** `CREATE` crée un Client, `LINK` rattache le Lead à un Client existant. */
  mode: 'CREATE' | 'LINK'
  clientId?: string
  /** Confirme explicitement la création malgré des doublons probables. */
  acknowledgeDuplicates?: boolean
  /**
   * Exigés par le contrat V3 d'un Client, et jamais devinés : le type est un
   * choix humain, le téléphone complète un Lead qui n'en porte pas.
   */
  clientType?: 'residentiel' | 'commercial'
  phone?: string
}

export interface ConvertLeadResult {
  leadId: string
  leadStatus: LeadStatus
  leadVersion: number
  clientId: string
  clientNumber: string | null
  created: boolean
  linkedQuotes: number
}

export interface ChangeLeadStatusResult {
  leadId: string
  number: string | null
  status: LeadStatus
  legacyStatus: string
  version: number
  changedAt: string
}

export interface LeadContact {
  displayName: string
  phone?: string
  email?: string
  address?: {
    line1: string
    city?: string
    postalCode?: string
  }
  /** Géocodage automatique à la capture (section Vue d'ensemble) — absent tant que non géocodée. */
  latitude?: number
  longitude?: number
}

export interface LeadRecord {
  id: string
  number?: string
  status: LeadStatus
  /** Issue d'appel Sprint C, absente tant que la migration n'est pas active. */
  statusV4?: LeadStatusV4
  version: number
  lostReason?: LeadLostReason
  lostNote?: string
  /** Client issu de la conversion, lorsqu'elle a déjà eu lieu. */
  convertedClientId?: string
  convertedClientNumber?: string
  contact: LeadContact
  serviceType?: string
  message?: string
  origin?: string
  assignedEmployeeId?: string
  followUpAt?: string
  followUpNote?: string
  /** Prochaine action ouverte sur ce Lead, section 26. */
  reminder?: LeadReminder
  createdAt: string
  updatedAt: string
}

export type LeadReminderKind = 'FIRST_CONTACT' | 'ESCALATION' | 'DAILY' | 'MANUAL'

export interface LeadReminder {
  id: string
  kind: LeadReminderKind
  /** Monte d'un cran à chaque escalade : au-delà de 1, le retard est constaté. */
  level: number
  dueAt: string
  message?: string
}

/** Un rappel échu est un Lead qu'on est en train d'oublier. */
export function isReminderOverdue(reminder: LeadReminder, now: number = Date.now()): boolean {
  return new Date(reminder.dueAt).getTime() <= now
}

export interface CompleteLeadReminderCommand {
  reminderId: string
  note?: string
}

export interface LeadStats {
  total: number
  new: number
  contacted: number
  quoteSent: number
  won: number
  lost: number
}

export interface LeadQueryMeta {
  source: LeadDataSource
  retrievedAt: string
  fallbackReason?: string
  anomalies: number
}

export interface LeadPage {
  items: LeadRecord[]
  page: number
  pageSize: number
  total: number
  stats: LeadStats
  meta: LeadQueryMeta
}

export interface LeadDetailResult {
  lead: LeadRecord
  /** Soumissions déjà rattachées au Lead, sans en inventer aucune. */
  quotes: LeadLinkedQuote[]
  meta: LeadQueryMeta
}

export interface LeadLinkedQuote {
  id: string
  number: string
  status: string
  totalCents: number
  updatedAt: string
}

export interface LeadListFilters {
  page: number
  pageSize: number
  search?: string
  status?: LeadStatus
}

/**
 * Un Lead sans téléphone ni courriel ne peut pas être recontacté. La section 21
 * exige des « coordonnées suffisantes » pour qualifier, sans définir de règle de
 * blocage : l'anomalie est donc signalée, jamais corrigée.
 */
/**
 * Un Lead déjà converti ne l'est jamais deux fois, et un Lead archivé ne reçoit
 * plus d'action commerciale. Le serveur applique les mêmes règles.
 */
export function isLeadConvertible(lead: LeadRecord): boolean {
  return lead.status !== 'ARCHIVED' && !lead.convertedClientId
}

export function isLeadContactable(lead: LeadRecord): boolean {
  return Boolean(lead.contact.phone ?? lead.contact.email)
}

export interface CreateLeadCommand {
  idempotencyKey: string
  firstName: string
  lastName: string
  /** Exigé : téléphone ou courriel, la section 17 du document 07. */
  phone?: string
  email?: string
  address?: string
  city?: string
  postalCode?: string
  serviceType?: string
  message?: string
  source?: string
}

export interface CreateLeadResult {
  leadId: string
  number: string
  displayName: string
}
